import RestartAltRounded from '@mui/icons-material/RestartAltRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { RichTextEditor } from '../../components/RichTextEditor';
import { errorMessage, useNotify } from '../../lib/notify';
import { htmlText } from '../../lib/richText';
import { PLACEHOLDERS } from '../whatsapp/messages';
import { WHATSAPP_MODELS } from '../whatsapp/WhatsappMessages';
import { useEmailAdmin, type EmailDocumentType, type EmailStatus } from './api';

/**
 * Settings → Email → Messages: per kind of document, the subject and rich-text message its Email
 * button starts with (each starts from its own default). Documents without a mail server are listed
 * too, marked so.
 */
export function EmailTemplates({ status, canManage }: { status: EmailStatus; canManage: boolean }) {
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Typography variant="h6">Messages</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          The subject and message each Email button starts with. Each document has its own; change
          the wording, sizes, bold text or tables as you like. You can use{' '}
          {PLACEHOLDERS.map((p, i) => (
            <span key={p.key}>
              {i > 0 && ', '}
              <Tooltip title={p.label}>
                <code>{`{${p.key}}`}</code>
              </Tooltip>
            </span>
          ))}
          .
        </Typography>
        <Stack spacing={0} sx={{ mt: 2 }}>
          {WHATSAPP_MODELS.map((m) => (
            <TemplateRow
              key={m.type}
              type={m.type}
              label={m.label}
              status={status}
              canManage={canManage}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function TemplateRow({
  type,
  label,
  status,
  canManage,
}: {
  type: EmailDocumentType;
  label: string;
  status: EmailStatus;
  canManage: boolean;
}) {
  const notify = useNotify();
  const admin = useEmailAdmin();
  const saved = status.templates[type];
  const [open, setOpen] = useState(false);
  // Edits in progress; null shows what is saved.
  const [subject, setSubject] = useState<string | null>(null);
  const [body, setBody] = useState<string | null>(null);
  const subjectText = subject ?? saved.subject;
  const bodyText = body ?? saved.body;
  const custom = status.customized.includes(type);
  const sender = status.documents[type];
  const busy = admin.settings.isPending;
  const changed = subjectText.trim() !== saved.subject || bodyText.trim() !== saved.body;
  // Bumped when edits are dropped so the editor shows the saved message again.
  const [generation, setGeneration] = useState(0);
  const clear = () => {
    setSubject(null);
    setBody(null);
    setGeneration((g) => g + 1);
  };
  const save = (value: { subject: string; body: string } | null, done: string) =>
    admin.settings.mutate(
      { templates: { [type]: value }, version: status.version },
      {
        onSuccess: () => {
          clear();
          notify.success(done);
        },
        onError: (e) => notify.error(errorMessage(e)),
      },
    );

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', py: 1.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 600, flex: 1 }}>{label}</Typography>
        {custom && <Chip size="small" variant="outlined" label="Own message" />}
        <Chip
          size="small"
          variant="outlined"
          color={sender ? 'primary' : 'default'}
          label={sender ? sender.name : 'No server'}
        />
        <Button size="small" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? 'Hide' : 'Message'}
        </Button>
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          <TextField
            label="Subject"
            value={subjectText}
            onChange={(e) => setSubject(e.target.value)}
            disabled={!canManage}
            slotProps={{ htmlInput: { maxLength: 300, 'aria-label': `${label} subject` } }}
          />
          <RichTextEditor
            key={generation}
            label={`${label} message`}
            value={bodyText}
            onChange={setBody}
            disabled={!canManage}
            placeholders={PLACEHOLDERS.map((p) => `{${p.key}}`)}
          />
          {canManage && (
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
              {custom && (
                <Button
                  size="small"
                  startIcon={<RestartAltRounded />}
                  disabled={busy}
                  onClick={() => save(null, 'Default message back')}
                >
                  Use default
                </Button>
              )}
              {changed && (
                <>
                  <Button size="small" disabled={busy} onClick={clear}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={busy || !subjectText.trim() || !htmlText(bodyText)}
                    onClick={() =>
                      save({ subject: subjectText.trim(), body: bodyText.trim() }, 'Message saved')
                    }
                  >
                    Save
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}
